import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ToastProvider } from '@osago/ui';

import DashboardPage from './pages/dashboardPage';
import MessagesPage from './pages/messagesPage';
import SingleMessagesPage from './pages/singleMessagePage';
import LoginPage from './pages/loginPage';
import PassResetPage from './pages/passResetPage';
import PassResetConfirmPage from './pages/passResetConfirmPage';
import NotFoundPage from './pages/notFoundPage';

import PrivateRoute from './components/privateRoute';
import SessionGuard from './components/sessionGuard';

import '@osago/ui/styles/fonts.css';
import './styles/main.src.css';
import { YandexMetrika } from './components/YandexMetrika';

const SESSION_ID_PARAM = /(sessionId=)[^&#"\s]*/gi;

function scrubSessionId<T>(payload: T): T {
  return JSON.parse(JSON.stringify(payload).replace(SESSION_ID_PARAM, '$1[Filtered]'));
}

const App = () => {

  // Sentry
  if (import.meta.env.MODE === 'production') {
    import("@sentry/react").then((Sentry) => {
      Sentry.init({
        dsn: "https://b629c411bd8f64d484e2293f13364ead@sentry.osagotop.ru/5",
        environment: import.meta.env.MODE,
        release: `osago-user-lk@${__APP_VERSION__}`,
        tracesSampleRate: 0.1,
        profilesSampleRate: 0.1,
        sampleRate: 0.1,
        sendDefaultPii: false,
        denyUrls: [
          /extensions\//i,
          /^chrome:\/\//i,
          /^chrome-extension:\/\//i,
          /^moz-extension:\/\//i,
          /^safari-extension:\/\//i,
        ],

        ignoreErrors: [
          // Browser extensions
          'top.GLOBALS',
          'canvas.contentDocument',
          'MyApp_RemoveAllHighlights',
          'atomicFindClose',
          'mechanismauto',
          // ResizeObserver
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          // Common non-critical errors
          'Non-Error promise rejection captured',
          'Non-Error exception captured',
        ],

        beforeSend(event) {
          // Ignore from extensions
          if (event.exception?.values?.[0]?.stacktrace?.frames?.some(
            frame => frame.filename?.includes('extension')
          )) {
            return null;
          }

          // Ignore HTTP errors (кроме критичных)
          // const error = hint?.originalException;
          // if (error && typeof error === 'object' && 'response' in error) {
          //     const response = error.response;
          //     if (response && typeof response === 'object' && 'status' in response) {
          //         const status = response.status;
          //         if (typeof status === 'number' && status >= 400 && status < 500 && ![401, 403].includes(status)) {
          //             return null;
          //         }
          //     }
          // }

          const url = event.request?.url;
          if (url && (url.includes('chrome-extension'))) {
            return null;
          }

          event.contexts = {
            ...event.contexts,
            custom: {
              userAgent: navigator.userAgent,
              screenResolution: `${window.screen.width}x${window.screen.height}`,
              viewport: `${window.innerWidth}x${window.innerHeight}`,
            }
          };

          return scrubSessionId(event);
        },

        beforeSendTransaction: (event) => scrubSessionId(event),

        beforeBreadcrumb: (breadcrumb) => scrubSessionId(breadcrumb),

        integrations: [
          Sentry.dedupeIntegration(),
          Sentry.browserTracingIntegration()
        ],
      });
    });
  }

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ToastProvider>
        <SessionGuard />
        <YandexMetrika />
        <Routes>
          <Route path="/" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          <Route path="/messages" element={<PrivateRoute><MessagesPage /></PrivateRoute>} />
          <Route path="/messages/:id" element={<PrivateRoute><SingleMessagesPage /></PrivateRoute>} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/passreset" element={<PassResetPage />} />
          <Route path="/passreset/confirm" element={<PassResetConfirmPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ToastProvider>
    </BrowserRouter>
  )
};

export default App;
